function getFocusableElements(container) {
  return Array.from(
    container.querySelectorAll(
      "summary, a[href], button:enabled, [tabindex]:not([tabindex^='-']), [draggable], area, input:not([type=hidden]):enabled, select:enabled, textarea:enabled, object, iframe"
    )
  );
}

class SectionId {
  static #separator = '__';

  // for a qualified section id (e.g. 'template--22224696705326__main'), return just the section id (e.g. 'template--22224696705326')
  static parseId(qualifiedSectionId) {
    return qualifiedSectionId.split(SectionId.#separator)[0];
  }

  // for a qualified section id (e.g. 'template--22224696705326__main'), return just the section name (e.g. 'main')
  static parseSectionName(qualifiedSectionId) {
    return qualifiedSectionId.split(SectionId.#separator)[1];
  }

  // for a section id (e.g. 'template--22224696705326') and a section name (e.g. 'recommended-products'), return a qualified section id (e.g. 'template--22224696705326__recommended-products')
  static getIdForSection(sectionId, sectionName) {
    return `${sectionId}${SectionId.#separator}${sectionName}`;
  }
}

class HTMLUpdateUtility {
  /**
   * Used to swap an HTML node with a new node.
   * The new node is inserted as a previous sibling to the old node, the old node is hidden, and then the old node is removed.
   *
   * The function currently uses a double buffer approach, but this should be replaced by a view transition once it is more widely supported https://developer.mozilla.org/en-US/docs/Web/API/View_Transitions_API
   */
  static viewTransition(oldNode, newContent, preProcessCallbacks = [], postProcessCallbacks = []) {
    preProcessCallbacks?.forEach((callback) => callback(newContent));

    const newNodeWrapper = document.createElement('div');
    HTMLUpdateUtility.setInnerHTML(newNodeWrapper, newContent.outerHTML);
    const newNode = newNodeWrapper.firstChild;

    // dedupe IDs
    const uniqueKey = Date.now();
    oldNode.querySelectorAll('[id], [form]').forEach((element) => {
      element.id && (element.id = `${element.id}-${uniqueKey}`);
      element.form && element.setAttribute('form', `${element.form.getAttribute('id')}-${uniqueKey}`);
    });

    oldNode.parentNode.insertBefore(newNode, oldNode);
    oldNode.style.display = 'none';

    postProcessCallbacks?.forEach((callback) => callback(newNode));

    setTimeout(() => oldNode.remove(), 500);
  }

  // Sets inner HTML and reinjects the script tags to allow execution. By default, scripts are disabled when using element.innerHTML.
  static setInnerHTML(element, html) {
    element.innerHTML = html;
    element.querySelectorAll('script').forEach((oldScriptTag) => {
      const newScriptTag = document.createElement('script');
      Array.from(oldScriptTag.attributes).forEach((attribute) => {
        newScriptTag.setAttribute(attribute.name, attribute.value);
      });
      newScriptTag.appendChild(document.createTextNode(oldScriptTag.innerHTML));
      oldScriptTag.parentNode.replaceChild(newScriptTag, oldScriptTag);
    });
  }
}

document.querySelectorAll('[id^="Details-"] summary').forEach((summary) => {
  summary.setAttribute('role', 'button');
  summary.setAttribute('aria-expanded', summary.parentNode.hasAttribute('open'));

  if (summary.nextElementSibling.getAttribute('id')) {
    summary.setAttribute('aria-controls', summary.nextElementSibling.id);
  }

  summary.addEventListener('click', (event) => {
    event.currentTarget.setAttribute('aria-expanded', !event.currentTarget.closest('details').hasAttribute('open'));
  });

  if (summary.closest('header-drawer, menu-drawer')) return;
  summary.parentElement.addEventListener('keyup', onKeyUpEscape);
});

const trapFocusHandlers = {};

function trapFocus(container, elementToFocus = container) {
  var elements = getFocusableElements(container);
  var first = elements[0];
  var last = elements[elements.length - 1];

  removeTrapFocus();

  trapFocusHandlers.focusin = (event) => {
    if (event.target !== container && event.target !== last && event.target !== first) return;

    document.addEventListener('keydown', trapFocusHandlers.keydown);
  };

  trapFocusHandlers.focusout = function () {
    document.removeEventListener('keydown', trapFocusHandlers.keydown);
  };

  trapFocusHandlers.keydown = function (event) {
    if (event.code.toUpperCase() !== 'TAB') return; // If not TAB key
    // On the last focusable element and tab forward, focus the first element.
    if (event.target === last && !event.shiftKey) {
      event.preventDefault();
      first.focus();
    }

    //  On the first focusable element and tab backward, focus the last element.
    if ((event.target === container || event.target === first) && event.shiftKey) {
      event.preventDefault();
      last.focus();
    }
  };

  document.addEventListener('focusout', trapFocusHandlers.focusout);
  document.addEventListener('focusin', trapFocusHandlers.focusin);

  elementToFocus.focus();

  if (
    elementToFocus.tagName === 'INPUT' &&
    ['search', 'text', 'email', 'url'].includes(elementToFocus.type) &&
    elementToFocus.value
  ) {
    elementToFocus.setSelectionRange(0, elementToFocus.value.length);
  }
}

// Here run the querySelector to figure out if the browser supports :focus-visible or not and run code based on it.
try {
  document.querySelector(':focus-visible');
} catch (e) {
  focusVisiblePolyfill();
}

function focusVisiblePolyfill() {
  const navKeys = [
    'ARROWUP',
    'ARROWDOWN',
    'ARROWLEFT',
    'ARROWRIGHT',
    'TAB',
    'ENTER',
    'SPACE',
    'ESCAPE',
    'HOME',
    'END',
    'PAGEUP',
    'PAGEDOWN',
  ];
  let currentFocusedElement = null;
  let mouseClick = null;

  window.addEventListener('keydown', (event) => {
    if (navKeys.includes(event.code.toUpperCase())) {
      mouseClick = false;
    }
  });

  window.addEventListener('mousedown', (event) => {
    mouseClick = true;
  });

  window.addEventListener(
    'focus',
    () => {
      if (currentFocusedElement) currentFocusedElement.classList.remove('focused');

      if (mouseClick) return;

      currentFocusedElement = document.activeElement;
      currentFocusedElement.classList.add('focused');
    },
    true
  );
}

function pauseAllMedia() {
  document.querySelectorAll('.js-youtube').forEach((video) => {
    video.contentWindow.postMessage('{"event":"command","func":"' + 'pauseVideo' + '","args":""}', '*');
  });
  document.querySelectorAll('.js-vimeo').forEach((video) => {
    video.contentWindow.postMessage('{"method":"pause"}', '*');
  });
  document.querySelectorAll('video').forEach((video) => video.pause());
  document.querySelectorAll('product-model').forEach((model) => {
    if (model.modelViewerUI) model.modelViewerUI.pause();
  });
}

function removeTrapFocus(elementToFocus = null) {
  document.removeEventListener('focusin', trapFocusHandlers.focusin);
  document.removeEventListener('focusout', trapFocusHandlers.focusout);
  document.removeEventListener('keydown', trapFocusHandlers.keydown);

  if (elementToFocus) elementToFocus.focus();
}

function onKeyUpEscape(event) {
  if (event.code.toUpperCase() !== 'ESCAPE') return;

  const openDetailsElement = event.target.closest('details[open]');
  if (!openDetailsElement) return;

  const summaryElement = openDetailsElement.querySelector('summary');
  openDetailsElement.removeAttribute('open');
  summaryElement.setAttribute('aria-expanded', false);
  summaryElement.focus();
}

class QuantityInput extends HTMLElement {
  constructor() {
    super();
    this.input = this.querySelector('input');
    this.changeEvent = new Event('change', { bubbles: true });
    this.input.addEventListener('change', this.onInputChange.bind(this));
    this.querySelectorAll('button').forEach((button) =>
      button.addEventListener('click', this.onButtonClick.bind(this))
    );
  }

  quantityUpdateUnsubscriber = undefined;

  connectedCallback() {
    this.validateQtyRules();
    this.quantityUpdateUnsubscriber = subscribe(PUB_SUB_EVENTS.quantityUpdate, this.validateQtyRules.bind(this));
  }

  disconnectedCallback() {
    if (this.quantityUpdateUnsubscriber) {
      this.quantityUpdateUnsubscriber();
    }
  }

  onInputChange(event) {
    this.validateQtyRules();
  }

  onButtonClick(event) {
    event.preventDefault();
    const previousValue = this.input.value;
    const maxQty = 5;

    if (event.target.name === 'plus') {

      if (previousValue >= maxQty) {
        this.input.value = maxQty;

        const index = this.input.dataset.index;
        const cartErrorEl = document.querySelector(`#CartDrawer-LineItemError-${index} .cart-item__error-text`);
        const container = this.closest('.pdp-buttons-container');
        const productForm = container?.querySelector('product-form');

        if (cartErrorEl) {
          cartErrorEl.textContent = `You can only add maximum ${maxQty} quantities.`;
          setTimeout(() => {
            cartErrorEl.textContent = '';
          }, 8000);
        } else if (!cartErrorEl && productForm) {
          productForm.handleErrorMessage(`You can only add maximum ${maxQty} quantities.`);
        }
        return;
      }

      if (parseInt(this.input.dataset.min) > parseInt(this.input.step) && this.input.value == 0) {
        this.input.value = this.input.dataset.min;
      } else {
        this.input.stepUp();
      }
    } else {
      this.input.stepDown();
    }

    if (previousValue !== this.input.value) this.input.dispatchEvent(this.changeEvent);

    if (this.input.dataset.min === previousValue && event.target.name === 'minus') {
      this.input.value = parseInt(this.input.min);
    }
  }

  validateQtyRules() {
    const value = parseInt(this.input.value);
    if (this.input.min) {
      const buttonMinus = this.querySelector(".quantity__button[name='minus']");
      buttonMinus.classList.toggle('disabled', parseInt(value) <= parseInt(this.input.min));
    }
    if (this.input.max) {
      const max = parseInt(this.input.max);
      const buttonPlus = this.querySelector(".quantity__button[name='plus']");
      buttonPlus.classList.toggle('disabled', value >= max);
    }
  }
}

customElements.define('quantity-input', QuantityInput);

function debounce(fn, wait) {
  let t;
  return (...args) => {
    clearTimeout(t);
    t = setTimeout(() => fn.apply(this, args), wait);
  };
}

function throttle(fn, delay) {
  let lastCall = 0;
  return function (...args) {
    const now = new Date().getTime();
    if (now - lastCall < delay) {
      return;
    }
    lastCall = now;
    return fn(...args);
  };
}

function fetchConfig(type = 'json') {
  return {
    method: 'POST',
    headers: { 'Content-Type': 'application/json', Accept: `application/${type}` },
  };
}

/*
 * Shopify Common JS
 *
 */
if (typeof window.Shopify == 'undefined') {
  window.Shopify = {};
}

Shopify.bind = function (fn, scope) {
  return function () {
    return fn.apply(scope, arguments);
  };
};

Shopify.setSelectorByValue = function (selector, value) {
  for (var i = 0, count = selector.options.length; i < count; i++) {
    var option = selector.options[i];
    if (value == option.value || value == option.innerHTML) {
      selector.selectedIndex = i;
      return i;
    }
  }
};

Shopify.addListener = function (target, eventName, callback) {
  target.addEventListener
    ? target.addEventListener(eventName, callback, false)
    : target.attachEvent('on' + eventName, callback);
};

Shopify.postLink = function (path, options) {
  options = options || {};
  var method = options['method'] || 'post';
  var params = options['parameters'] || {};

  var form = document.createElement('form');
  form.setAttribute('method', method);
  form.setAttribute('action', path);

  for (var key in params) {
    var hiddenField = document.createElement('input');
    hiddenField.setAttribute('type', 'hidden');
    hiddenField.setAttribute('name', key);
    hiddenField.setAttribute('value', params[key]);
    form.appendChild(hiddenField);
  }
  document.body.appendChild(form);
  form.submit();
  document.body.removeChild(form);
};

Shopify.CountryProvinceSelector = function (country_domid, province_domid, options) {
  this.countryEl = document.getElementById(country_domid);
  this.provinceEl = document.getElementById(province_domid);
  this.provinceContainer = document.getElementById(options['hideElement'] || province_domid);

  Shopify.addListener(this.countryEl, 'change', Shopify.bind(this.countryHandler, this));

  this.initCountry();
  this.initProvince();
};

Shopify.CountryProvinceSelector.prototype = {
  initCountry: function () {
    var value = this.countryEl.getAttribute('data-default');
    Shopify.setSelectorByValue(this.countryEl, value);
    this.countryHandler();
  },

  initProvince: function () {
    var value = this.provinceEl.getAttribute('data-default');
    if (value && this.provinceEl.options.length > 0) {
      Shopify.setSelectorByValue(this.provinceEl, value);
    }
  },

  countryHandler: function (e) {
    var opt = this.countryEl.options[this.countryEl.selectedIndex];
    var raw = opt.getAttribute('data-provinces');
    var provinces = JSON.parse(raw);

    this.clearOptions(this.provinceEl);
    if (provinces && provinces.length == 0) {
      this.provinceContainer.style.display = 'none';
    } else {
      for (var i = 0; i < provinces.length; i++) {
        var opt = document.createElement('option');
        opt.value = provinces[i][0];
        opt.innerHTML = provinces[i][1];
        this.provinceEl.appendChild(opt);
      }

      this.provinceContainer.style.display = '';
    }
  },

  clearOptions: function (selector) {
    while (selector.firstChild) {
      selector.removeChild(selector.firstChild);
    }
  },

  setOptions: function (selector, values) {
    for (var i = 0, count = values.length; i < values.length; i++) {
      var opt = document.createElement('option');
      opt.value = values[i];
      opt.innerHTML = values[i];
      selector.appendChild(opt);
    }
  },
};

class MenuDrawer extends HTMLElement {
  constructor() {
    super();

    this.mainDetailsToggle = this.querySelector('details');
    this.menuCloseBtn = this.querySelector(".mobile-facets__close-btn");

    if (this.menuCloseBtn) {
      this.menuCloseBtn.addEventListener("click", (event) => {
        event.preventDefault();
        this.closeMenuDrawer(this.mainDetailsToggle.querySelector('summary'));
      });
    }

    this.addEventListener('keyup', this.onKeyUp.bind(this));
    this.addEventListener('focusout', this.onFocusOut.bind(this));
    this.bindEvents();
  }

  bindEvents() {
    this.querySelectorAll('summary').forEach((summary) =>
      summary.addEventListener('click', this.onSummaryClick.bind(this))
    );
    this.querySelectorAll(
      'button:not(.localization-selector):not(.country-selector__close-button):not(.country-filter__reset-button)'
    ).forEach((button) => button.addEventListener('click', this.onCloseButtonClick.bind(this)));
  }

  onKeyUp(event) {
    if (event.code.toUpperCase() !== 'ESCAPE') return;

    const openDetailsElement = event.target.closest('details[open]');
    if (!openDetailsElement) return;

    openDetailsElement === this.mainDetailsToggle
      ? this.closeMenuDrawer(event, this.mainDetailsToggle.querySelector('summary'))
      : this.closeSubmenu(openDetailsElement);
  }

  onSummaryClick(event) {
    const summaryElement = event.currentTarget;
    const detailsElement = summaryElement.parentNode;
    const parentMenuElement = detailsElement.closest('.has-submenu');
    const isOpen = detailsElement.hasAttribute('open');
    const reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)');

    function addTrapFocus() {
      trapFocus(summaryElement.nextElementSibling, detailsElement.querySelector('button'));
      summaryElement.nextElementSibling.removeEventListener('transitionend', addTrapFocus);
    }

    if (detailsElement === this.mainDetailsToggle) {
      if (isOpen) event.preventDefault();
      isOpen ? this.closeMenuDrawer(event, summaryElement) : this.openMenuDrawer(summaryElement);

      if (window.matchMedia('(max-width: 990px)')) {
        document.documentElement.style.setProperty('--viewport-height', `${window.innerHeight}px`);
      }
    } else {
      setTimeout(() => {
        detailsElement.classList.add('menu-opening');
        summaryElement.setAttribute('aria-expanded', true);
        parentMenuElement && parentMenuElement.classList.add('submenu-open');
        !reducedMotion || reducedMotion.matches
          ? addTrapFocus()
          : summaryElement.nextElementSibling.addEventListener('transitionend', addTrapFocus);
      }, 100);
    }
  }

  openMenuDrawer(summaryElement) {
    setTimeout(() => {
      this.mainDetailsToggle.classList.add('menu-opening');
    });
    summaryElement.setAttribute('aria-expanded', true);
    trapFocus(this.mainDetailsToggle, summaryElement);
    document.body.classList.add(`overflow-hidden-${this.dataset.breakpoint}`);
  }

  closeMenuDrawer(event, elementToFocus = false) {
    if (event === undefined) return;

    this.mainDetailsToggle.classList.remove('menu-opening');
    this.mainDetailsToggle.querySelectorAll('details').forEach((details) => {
      details.removeAttribute('open');
      details.classList.remove('menu-opening');
    });
    this.mainDetailsToggle.querySelectorAll('.submenu-open').forEach((submenu) => {
      submenu.classList.remove('submenu-open');
    });
    document.body.classList.remove(`overflow-hidden-${this.dataset.breakpoint}`);
    removeTrapFocus(elementToFocus);
    this.closeAnimation(this.mainDetailsToggle);

    if (event instanceof KeyboardEvent) elementToFocus?.setAttribute('aria-expanded', false);
  }

  onFocusOut() {
    setTimeout(() => {
      if (this.mainDetailsToggle.hasAttribute('open') && !this.mainDetailsToggle.contains(document.activeElement))
        this.closeMenuDrawer();
    });
  }

  onCloseButtonClick(event) {
    event.preventDefault();
    if (event.target !== this.menuCloseBtn) {
      const detailsElement = event.currentTarget.closest('details');
      this.closeSubmenu(detailsElement);
    }
  }

  closeSubmenu(detailsElement) {
    const parentMenuElement = detailsElement.closest('.submenu-open');
    parentMenuElement && parentMenuElement.classList.remove('submenu-open');
    detailsElement.classList.remove('menu-opening');
    detailsElement.querySelector('summary').setAttribute('aria-expanded', false);
    removeTrapFocus(detailsElement.querySelector('summary'));
    this.closeAnimation(detailsElement);
  }

  closeAnimation(detailsElement) {
    let animationStart;

    const handleAnimation = (time) => {
      if (animationStart === undefined) {
        animationStart = time;
      }

      const elapsedTime = time - animationStart;

      if (elapsedTime < 400) {
        window.requestAnimationFrame(handleAnimation);
      } else {
        detailsElement.removeAttribute('open');
        if (detailsElement.closest('details[open]')) {
          trapFocus(detailsElement.closest('details[open]'), detailsElement.querySelector('summary'));
        }
      }
    };

    window.requestAnimationFrame(handleAnimation);
  }
}

customElements.define('menu-drawer', MenuDrawer);

class HeaderDrawer extends MenuDrawer {
  constructor() {
    super();
  }

  openMenuDrawer(summaryElement) {
    this.header = this.header || document.querySelector('.section-header');
    this.borderOffset =
      this.borderOffset || this.closest('.header-wrapper').classList.contains('header-wrapper--border-bottom') ? 1 : 0;
    document.documentElement.style.setProperty(
      '--header-bottom-position',
      `${parseInt(this.header.getBoundingClientRect().bottom - this.borderOffset)}px`
    );
    this.header.classList.add('menu-open');

    setTimeout(() => {
      this.mainDetailsToggle.classList.add('menu-opening');
      const pdpButtonsContainer = document.querySelector('.pdp-buttons-container');
      if (pdpButtonsContainer) {
        pdpButtonsContainer.classList.add('hidden');
      }
    });

    summaryElement.setAttribute('aria-expanded', true);
    window.addEventListener('resize', this.onResize);
    trapFocus(this.mainDetailsToggle, summaryElement);
    document.body.classList.add(`overflow-hidden-${this.dataset.breakpoint}`);
  }

  closeMenuDrawer(event, elementToFocus) {
    if (!elementToFocus) return;
    super.closeMenuDrawer(event, elementToFocus);
    this.header.classList.remove('menu-open');
    window.removeEventListener('resize', this.onResize);
    const pdpButtonsContainer = document.querySelector('.pdp-buttons-container');
    if (pdpButtonsContainer) {
      pdpButtonsContainer.classList.remove('hidden');
    }
  }

  onResize = () => {
    this.header &&
      document.documentElement.style.setProperty(
        '--header-bottom-position',
        `${parseInt(this.header.getBoundingClientRect().bottom - this.borderOffset)}px`
      );
    document.documentElement.style.setProperty('--viewport-height', `${window.innerHeight}px`);
  };
}

customElements.define('header-drawer', HeaderDrawer);

// Script For Our Products Svg 

document.querySelectorAll('.menu-drawer__menu-item').forEach(item => {
  const svg = item.querySelector('.svg-size');
  if (svg) svg.classList.remove('rotated');

  item.addEventListener('click', () => {
    svg?.classList.toggle('rotated');
  });
});

class ModalDialog extends HTMLElement {
  constructor() {
    super();
    this.querySelector('[id^="ModalClose-"]').addEventListener('click', this.hide.bind(this, false));
    this.addEventListener('keyup', (event) => {
      if (event.code.toUpperCase() === 'ESCAPE') this.hide();
    });
    if (this.classList.contains('media-modal')) {
      this.addEventListener('pointerup', (event) => {
        if (event.pointerType === 'mouse' && !event.target.closest('deferred-media, product-model')) this.hide();
      });
    } else {
      this.addEventListener('click', (event) => {
        if (event.target === this) this.hide();
      });
    }
  }

  connectedCallback() {
    if (this.moved) return;
    this.moved = true;
    this.dataset.section = this.closest('.shopify-section').id.replace('shopify-section-', '');
    document.body.appendChild(this);
  }

  show(opener) {
    this.openedBy = opener;
    const popup = this.querySelector('.template-popup');
    document.body.classList.add('overflow-hidden');
    this.setAttribute('open', '');
    if (popup) popup.loadContent();
    trapFocus(this, this.querySelector('[role="dialog"]'));
    window.pauseAllMedia();
  }

  hide() {
    document.body.classList.remove('overflow-hidden');
    document.body.dispatchEvent(new CustomEvent('modalClosed'));
    this.removeAttribute('open');
    removeTrapFocus(this.openedBy);
    window.pauseAllMedia();
  }
}
customElements.define('modal-dialog', ModalDialog);

class BulkModal extends HTMLElement {
  constructor() {
    super();
  }

  connectedCallback() {
    const handleIntersection = (entries, observer) => {
      if (!entries[0].isIntersecting) return;
      observer.unobserve(this);
      if (this.innerHTML.trim() === '') {
        const productUrl = this.dataset.url.split('?')[0];
        fetch(`${productUrl}?section_id=bulk-quick-order-list`)
          .then((response) => response.text())
          .then((responseText) => {
            const html = new DOMParser().parseFromString(responseText, 'text/html');
            const sourceQty = html.querySelector('.quick-order-list-container').parentNode;
            this.innerHTML = sourceQty.innerHTML;
          })
          .catch((e) => {
            console.error(e);
          });
      }
    };

    new IntersectionObserver(handleIntersection.bind(this)).observe(
      document.querySelector(`#QuickBulk-${this.dataset.productId}-${this.dataset.sectionId}`)
    );
  }
}

customElements.define('bulk-modal', BulkModal);

class ModalOpener extends HTMLElement {
  constructor() {
    super();

    const button = this.querySelector('button');

    if (!button) return;
    button.addEventListener('click', () => {
      const modal = document.querySelector(this.getAttribute('data-modal'));
      if (modal) modal.show(button);
    });
  }
}
customElements.define('modal-opener', ModalOpener);

class DeferredMedia extends HTMLElement {
  constructor() {
    super();
    const poster = this.querySelector('[id^="Deferred-Poster-"]');
    if (!poster) return;
    poster.addEventListener('click', this.loadContent.bind(this));
  }

  loadContent(focus = true) {
    window.pauseAllMedia();
    if (!this.getAttribute('loaded')) {
      const content = document.createElement('div');
      content.appendChild(this.querySelector('template').content.firstElementChild.cloneNode(true));

      this.setAttribute('loaded', true);
      const deferredElement = this.appendChild(content.querySelector('video, model-viewer, iframe'));
      if (focus) deferredElement.focus();
      if (deferredElement.nodeName == 'VIDEO' && deferredElement.getAttribute('autoplay')) {
        // force autoplay for safari
        deferredElement.play();
      }
    }
  }
}

customElements.define('deferred-media', DeferredMedia);

class SliderComponent extends HTMLElement {
  constructor() {
    super();
    this.slider = this.querySelector('[id^="Slider-"]');
    this.sliderItems = this.querySelectorAll('[id^="Slide-"]');
    this.enableSliderLooping = false;
    this.currentPageElement = this.querySelector('.slider-counter--current');
    this.pageTotalElement = this.querySelector('.slider-counter--total');
    this.prevButton = this.querySelector('button[name="previous"]');
    this.nextButton = this.querySelector('button[name="next"]');

    if (!this.slider || !this.nextButton) return;

    this.initPages();
    const resizeObserver = new ResizeObserver((entries) => this.initPages());
    resizeObserver.observe(this.slider);

    this.slider.addEventListener('scroll', this.update.bind(this));
    this.prevButton.addEventListener('click', this.onButtonClick.bind(this));
    this.nextButton.addEventListener('click', this.onButtonClick.bind(this));
  }

  initPages() {
    this.sliderItemsToShow = Array.from(this.sliderItems).filter((element) => element.clientWidth > 0);
    if (this.sliderItemsToShow.length < 2) return;
    this.sliderItemOffset = this.sliderItemsToShow[1].offsetLeft - this.sliderItemsToShow[0].offsetLeft;
    this.slidesPerPage = Math.floor(
      (this.slider.clientWidth - this.sliderItemsToShow[0].offsetLeft) / this.sliderItemOffset
    );
    this.totalPages = this.sliderItemsToShow.length - this.slidesPerPage + 1;
    this.update();
  }

  resetPages() {
    this.sliderItems = this.querySelectorAll('[id^="Slide-"]');
    this.initPages();
  }

  update() {
    // Temporarily prevents unneeded updates resulting from variant changes
    // This should be refactored as part of https://github.com/Shopify/dawn/issues/2057
    if (!this.slider || !this.nextButton) return;

    const previousPage = this.currentPage;
    this.currentPage = Math.round(this.slider.scrollLeft / this.sliderItemOffset) + 1;

    if (this.currentPageElement && this.pageTotalElement) {
      this.currentPageElement.textContent = this.currentPage;
      this.pageTotalElement.textContent = this.totalPages;
    }

    if (this.currentPage != previousPage) {
      this.dispatchEvent(
        new CustomEvent('slideChanged', {
          detail: {
            currentPage: this.currentPage,
            currentElement: this.sliderItemsToShow[this.currentPage - 1],
          },
        })
      );
    }

    if (this.enableSliderLooping) return;

    if (this.isSlideVisible(this.sliderItemsToShow[0]) && this.slider.scrollLeft === 0) {
      this.prevButton.setAttribute('disabled', 'disabled');
    } else {
      this.prevButton.removeAttribute('disabled');
    }

    if (this.isSlideVisible(this.sliderItemsToShow[this.sliderItemsToShow.length - 1])) {
      this.nextButton.setAttribute('disabled', 'disabled');
    } else {
      this.nextButton.removeAttribute('disabled');
    }
  }

  isSlideVisible(element, offset = 0) {
    const lastVisibleSlide = this.slider.clientWidth + this.slider.scrollLeft - offset;
    return element.offsetLeft + element.clientWidth <= lastVisibleSlide && element.offsetLeft >= this.slider.scrollLeft;
  }

  onButtonClick(event) {
    event.preventDefault();
    const step = event.currentTarget.dataset.step || 1;
    this.slideScrollPosition =
      event.currentTarget.name === 'next'
        ? this.slider.scrollLeft + step * this.sliderItemOffset
        : this.slider.scrollLeft - step * this.sliderItemOffset;
    this.setSlidePosition(this.slideScrollPosition);
  }

  setSlidePosition(position) {
    this.slider.scrollTo({
      left: position,
    });
  }
}

customElements.define('slider-component', SliderComponent);

class SlideshowComponent extends SliderComponent {
  constructor() {
    super();
    this.sliderControlWrapper = this.querySelector('.slider-buttons');
    this.enableSliderLooping = true;

    if (!this.sliderControlWrapper) return;

    this.sliderFirstItemNode = this.slider.querySelector('.slideshow__slide');
    if (this.sliderItemsToShow.length > 0) this.currentPage = 1;

    this.announcementBarSlider = this.querySelector('.announcement-bar-slider');
    // Value below should match --duration-announcement-bar CSS value
    this.announcerBarAnimationDelay = this.announcementBarSlider ? 250 : 0;

    this.sliderControlLinksArray = Array.from(this.sliderControlWrapper.querySelectorAll('.slider-counter__link'));
    this.sliderControlLinksArray.forEach((link) => link.addEventListener('click', this.linkToSlide.bind(this)));
    this.slider.addEventListener('scroll', this.setSlideVisibility.bind(this));
    this.setSlideVisibility();

    if (this.announcementBarSlider) {
      this.announcementBarArrowButtonWasClicked = false;

      this.reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)');
      this.reducedMotion.addEventListener('change', () => {
        if (this.slider.getAttribute('data-autoplay') === 'true') this.setAutoPlay();
      });

      [this.prevButton, this.nextButton].forEach((button) => {
        button.addEventListener(
          'click',
          () => {
            this.announcementBarArrowButtonWasClicked = true;
          },
          { once: true }
        );
      });
    }

    if (this.slider.getAttribute('data-autoplay') === 'true') this.setAutoPlay();
  }

  setAutoPlay() {
    this.autoplaySpeed = this.slider.dataset.speed * 1000;
    this.addEventListener('mouseover', this.focusInHandling.bind(this));
    this.addEventListener('mouseleave', this.focusOutHandling.bind(this));
    this.addEventListener('focusin', this.focusInHandling.bind(this));
    this.addEventListener('focusout', this.focusOutHandling.bind(this));

    if (this.querySelector('.slideshow__autoplay')) {
      this.sliderAutoplayButton = this.querySelector('.slideshow__autoplay');
      this.sliderAutoplayButton.addEventListener('click', this.autoPlayToggle.bind(this));
      this.autoplayButtonIsSetToPlay = true;
      this.play();
    } else {
      this.reducedMotion.matches || this.announcementBarArrowButtonWasClicked ? this.pause() : this.play();
    }
  }

  onButtonClick(event) {
    super.onButtonClick(event);
    this.wasClicked = true;

    const isFirstSlide = this.currentPage === 1;
    const isLastSlide = this.currentPage === this.sliderItemsToShow.length;

    if (!isFirstSlide && !isLastSlide) {
      this.applyAnimationToAnnouncementBar(event.currentTarget.name);
      return;
    }

    if (isFirstSlide && event.currentTarget.name === 'previous') {
      this.slideScrollPosition =
        this.slider.scrollLeft + this.sliderFirstItemNode.clientWidth * this.sliderItemsToShow.length;
    } else if (isLastSlide && event.currentTarget.name === 'next') {
      this.slideScrollPosition = 0;
    }

    this.setSlidePosition(this.slideScrollPosition);

    this.applyAnimationToAnnouncementBar(event.currentTarget.name);
  }

  setSlidePosition(position) {
    if (this.setPositionTimeout) clearTimeout(this.setPositionTimeout);
    this.setPositionTimeout = setTimeout(() => {
      this.slider.scrollTo({
        left: position,
      });
    }, this.announcerBarAnimationDelay);
  }

  update() {
    super.update();
    this.sliderControlButtons = this.querySelectorAll('.slider-counter__link');
    this.prevButton.removeAttribute('disabled');

    if (!this.sliderControlButtons.length) return;

    this.sliderControlButtons.forEach((link) => {
      link.classList.remove('slider-counter__link--active');
      link.removeAttribute('aria-current');
    });
    this.sliderControlButtons[this.currentPage - 1].classList.add('slider-counter__link--active');
    this.sliderControlButtons[this.currentPage - 1].setAttribute('aria-current', true);
  }

  autoPlayToggle() {
    this.togglePlayButtonState(this.autoplayButtonIsSetToPlay);
    this.autoplayButtonIsSetToPlay ? this.pause() : this.play();
    this.autoplayButtonIsSetToPlay = !this.autoplayButtonIsSetToPlay;
  }

  focusOutHandling(event) {
    if (this.sliderAutoplayButton) {
      const focusedOnAutoplayButton =
        event.target === this.sliderAutoplayButton || this.sliderAutoplayButton.contains(event.target);
      if (!this.autoplayButtonIsSetToPlay || focusedOnAutoplayButton) return;
      this.play();
    } else if (!this.reducedMotion.matches && !this.announcementBarArrowButtonWasClicked) {
      this.play();
    }
  }

  focusInHandling(event) {
    if (this.sliderAutoplayButton) {
      const focusedOnAutoplayButton =
        event.target === this.sliderAutoplayButton || this.sliderAutoplayButton.contains(event.target);
      if (focusedOnAutoplayButton && this.autoplayButtonIsSetToPlay) {
        this.play();
      } else if (this.autoplayButtonIsSetToPlay) {
        this.pause();
      }
    } else if (this.announcementBarSlider.contains(event.target)) {
      this.pause();
    }
  }

  play() {
    this.slider.setAttribute('aria-live', 'off');
    clearInterval(this.autoplay);
    this.autoplay = setInterval(this.autoRotateSlides.bind(this), this.autoplaySpeed);
  }

  pause() {
    this.slider.setAttribute('aria-live', 'polite');
    clearInterval(this.autoplay);
  }

  togglePlayButtonState(pauseAutoplay) {
    if (pauseAutoplay) {
      this.sliderAutoplayButton.classList.add('slideshow__autoplay--paused');
      this.sliderAutoplayButton.setAttribute('aria-label', window.accessibilityStrings.playSlideshow);
    } else {
      this.sliderAutoplayButton.classList.remove('slideshow__autoplay--paused');
      this.sliderAutoplayButton.setAttribute('aria-label', window.accessibilityStrings.pauseSlideshow);
    }
  }

  autoRotateSlides() {
    const slideScrollPosition =
      this.currentPage === this.sliderItems.length ? 0 : this.slider.scrollLeft + this.sliderItemOffset;

    this.setSlidePosition(slideScrollPosition);
    this.applyAnimationToAnnouncementBar();
  }

  setSlideVisibility(event) {
    this.sliderItemsToShow.forEach((item, index) => {
      const linkElements = item.querySelectorAll('a');
      if (index === this.currentPage - 1) {
        if (linkElements.length)
          linkElements.forEach((button) => {
            button.removeAttribute('tabindex');
          });
        item.setAttribute('aria-hidden', 'false');
        item.removeAttribute('tabindex');
      } else {
        if (linkElements.length)
          linkElements.forEach((button) => {
            button.setAttribute('tabindex', '-1');
          });
        item.setAttribute('aria-hidden', 'true');
        item.setAttribute('tabindex', '-1');
      }
    });
    this.wasClicked = false;
  }

  applyAnimationToAnnouncementBar(button = 'next') {
    if (!this.announcementBarSlider) return;

    const itemsCount = this.sliderItems.length;
    const increment = button === 'next' ? 1 : -1;

    const currentIndex = this.currentPage - 1;
    let nextIndex = (currentIndex + increment) % itemsCount;
    nextIndex = nextIndex === -1 ? itemsCount - 1 : nextIndex;

    const nextSlide = this.sliderItems[nextIndex];
    const currentSlide = this.sliderItems[currentIndex];

    const animationClassIn = 'announcement-bar-slider--fade-in';
    const animationClassOut = 'announcement-bar-slider--fade-out';

    const isFirstSlide = currentIndex === 0;
    const isLastSlide = currentIndex === itemsCount - 1;

    const shouldMoveNext = (button === 'next' && !isLastSlide) || (button === 'previous' && isFirstSlide);
    const direction = shouldMoveNext ? 'next' : 'previous';

    currentSlide.classList.add(`${animationClassOut}-${direction}`);
    nextSlide.classList.add(`${animationClassIn}-${direction}`);

    setTimeout(() => {
      currentSlide.classList.remove(`${animationClassOut}-${direction}`);
      nextSlide.classList.remove(`${animationClassIn}-${direction}`);
    }, this.announcerBarAnimationDelay * 2);
  }

  linkToSlide(event) {
    event.preventDefault();
    const slideScrollPosition =
      this.slider.scrollLeft +
      this.sliderFirstItemNode.clientWidth *
        (this.sliderControlLinksArray.indexOf(event.currentTarget) + 1 - this.currentPage);
    this.slider.scrollTo({
      left: slideScrollPosition,
    });
  }
}

customElements.define('slideshow-component', SlideshowComponent);

class VariantSelects extends HTMLElement {
  constructor() {
    super();
  }

  connectedCallback() {
    this.addEventListener('change', (event) => {
      const target = this.getInputForEventTarget(event.target);
      this.updateSelectionMetadata(event);

      publish(PUB_SUB_EVENTS.optionValueSelectionChange, {
        data: {
          event,
          target,
          selectedOptionValues: this.selectedOptionValues,
        },
      });
    });
  }

  updateSelectionMetadata({ target }) {
    const { value, tagName } = target;

    const variantOptionElement = document.querySelector('.sticky-button-product-variant');
    if (variantOptionElement){
      variantOptionElement.textContent = value;
    }

    if (tagName === 'SELECT' && target.selectedOptions.length) {
      Array.from(target.options)
        .find((option) => option.getAttribute('selected'))
        .removeAttribute('selected');
      target.selectedOptions[0].setAttribute('selected', 'selected');

      const swatchValue = target.selectedOptions[0].dataset.optionSwatchValue;
      const selectedDropdownSwatchValue = target
        .closest('.product-form__input')
        .querySelector('[data-selected-value] > .swatch');
      if (!selectedDropdownSwatchValue) return;
      if (swatchValue) {
        selectedDropdownSwatchValue.style.setProperty('--swatch--background', swatchValue);
        selectedDropdownSwatchValue.classList.remove('swatch--unavailable');
      } else {
        selectedDropdownSwatchValue.style.setProperty('--swatch--background', 'unset');
        selectedDropdownSwatchValue.classList.add('swatch--unavailable');
      }

      selectedDropdownSwatchValue.style.setProperty(
        '--swatch-focal-point',
        target.selectedOptions[0].dataset.optionSwatchFocalPoint || 'unset'
      );
    } else if (tagName === 'INPUT' && target.type === 'radio') {
      const selectedSwatchValue = target.closest(`.product-form__input`).querySelector('[data-selected-value]');
      if (selectedSwatchValue) selectedSwatchValue.innerHTML = value;
    }
  }

  getInputForEventTarget(target) {
    return target.tagName === 'SELECT' ? target.selectedOptions[0] : target;
  }

  get selectedOptionValues() {
    return Array.from(this.querySelectorAll('select option[selected], fieldset input:checked')).map(
      ({ dataset }) => dataset.optionValueId
    );
  }
}

customElements.define('variant-selects', VariantSelects);

class ProductRecommendations extends HTMLElement {
  observer = undefined;

  constructor() {
    super();
  }

  connectedCallback() {
    this.initializeRecommendations(this.dataset.productId);
  }

  initializeRecommendations(productId) {
    this.observer?.unobserve(this);
    this.observer = new IntersectionObserver(
      (entries, observer) => {
        if (!entries[0].isIntersecting) return;
        observer.unobserve(this);
        this.loadRecommendations(productId);
      },
      { rootMargin: '0px 0px 400px 0px' }
    );
    this.observer.observe(this);
  }

  loadRecommendations(productId) {
    fetch(`${this.dataset.url}&product_id=${productId}&section_id=${this.dataset.sectionId}`)
      .then((response) => response.text())
      .then((text) => {
        const html = document.createElement('div');
        html.innerHTML = text;
        const recommendations = html.querySelector('product-recommendations');

        if (recommendations?.innerHTML.trim().length) {
          this.innerHTML = recommendations.innerHTML;
        }

        if (!this.querySelector('slideshow-component') && this.classList.contains('complementary-products')) {
          this.remove();
        }

        if (html.querySelector('.grid__item')) {
          this.classList.add('product-recommendations--loaded');
        }
      })
      .catch((e) => {
        console.error(e);
      });
  }
}

customElements.define('product-recommendations', ProductRecommendations);

class AccountIcon extends HTMLElement {
  constructor() {
    super();

    this.icon = this.querySelector('.icon');
  }

  connectedCallback() {
    document.addEventListener('storefront:signincompleted', this.handleStorefrontSignInCompleted.bind(this));
  }

  handleStorefrontSignInCompleted(event) {
    if (event?.detail?.avatar) {
      this.icon?.replaceWith(event.detail.avatar.cloneNode());
    }
  }
}

customElements.define('account-icon', AccountIcon);

class BulkAdd extends HTMLElement {
  constructor() {
    super();
    this.queue = [];
    this.requestStarted = false;
    this.ids = [];
  }

  startQueue(id, quantity) {
    this.queue.push({ id, quantity });
    const interval = setInterval(() => {
      if (this.queue.length > 0) {
        if (!this.requestStarted) {
          this.sendRequest(this.queue);
        }
      } else {
        clearInterval(interval);
      }
    }, 250);
  }

  sendRequest(queue) {
    this.requestStarted = true;
    const items = {};
    queue.forEach((queueItem) => {
      items[parseInt(queueItem.id)] = queueItem.quantity;
    });
    this.queue = this.queue.filter((queueElement) => !queue.includes(queueElement));
    const quickBulkElement = this.closest('quick-order-list') || this.closest('quick-add-bulk');
    quickBulkElement.updateMultipleQty(items);
  }

  resetQuantityInput(id) {
    const input = this.querySelector(`#Quantity-${id}`);
    input.value = input.getAttribute('value');
    this.isEnterPressed = false;
  }

  setValidity(event, index, message) {
    event.target.setCustomValidity(message);
    event.target.reportValidity();
    this.resetQuantityInput(index);
    event.target.select();
  }

  validateQuantity(event) {
    const inputValue = parseInt(event.target.value);
    const index = event.target.dataset.index;

    if (inputValue < event.target.dataset.min) {
      this.setValidity(event, index, window.quickOrderListStrings.min_error.replace('[min]', event.target.dataset.min));
    } else if (inputValue > parseInt(event.target.max)) {
      this.setValidity(event, index, window.quickOrderListStrings.max_error.replace('[max]', event.target.max));
    } else if (inputValue % parseInt(event.target.step) != 0) {
      this.setValidity(event, index, window.quickOrderListStrings.step_error.replace('[step]', event.target.step));
    } else {
      event.target.setCustomValidity('');
      event.target.reportValidity();
      this.startQueue(index, inputValue);
    }
  }

  getSectionsUrl() {
    if (window.pageNumber) {
      return `${window.location.pathname}?page=${window.pageNumber}`;
    } else {
      return `${window.location.pathname}`;
    }
  }

  getSectionInnerHTML(html, selector) {
    return new DOMParser().parseFromString(html, 'text/html').querySelector(selector).innerHTML;
  }
}

if (!customElements.get('bulk-add')) {
  customElements.define('bulk-add', BulkAdd);
}

class SnakeBorderButton extends HTMLElement {
  constructor() {
    super();

    const checkoutButton = this.querySelector('#CartDrawer-Checkout');
    if (checkoutButton) {
      checkoutButton.addEventListener('click', function () {
        showOverlay();
      });
    }

    // Attach a shadow root with mode 'open'
    const shadow = this.attachShadow({ mode: 'open' });

    // Set default role if not provided
    if (!this.hasAttribute('role')) {
      this.setAttribute('role', 'button');
    }
    
    // Ensure the element is focusable
    if (!this.hasAttribute('tabindex')) {
      this.setAttribute('tabindex', '0');
    }
    
    // Add keyboard event handling
    this.addEventListener('keydown', (e) => {
      if (e.key === 'Enter' || e.key === ' ') {
        e.preventDefault();
        this.click();
      }
    });

    // Add template content to the shadow root
    shadow.innerHTML = `
      <style>
        :host {
          display: inline-block;
          position: relative;
          border-radius: 5px;
          outline: 1px solid #3B3529;
          outline-offset: -1.5px;
          cursor: pointer;
          text-transform: uppercase;
          box-sizing: border-box;
          overflow: hidden;
        }

        :host > span {
          border-radius: 50px;
        }

        span {
          position: absolute;
          z-index:2;
          background: var(--gold-gradient);
        }

        span.top {
          top: 0;
          left: 0;
          width: 100%;
          height: 2px;
          animation: topAnimation 4s linear infinite;
          transform: translateX(100%);
        }

        @keyframes topAnimation {
          0% { transform: translateX(100%); }
          3% { transform: translateX(70%); }
          12% { transform: translateX(70%); }
          15% { transform: translateX(75%); }
          24% { transform: translateX(75%); }
          28% { transform: translateX(100%); }
          100% { transform: translateX(100%); }
        }

        span.right {
          top: 0;
          right: 0;
          width: 2px;
          height: 100%;
          animation: rightAnimation 4s linear infinite;
          transform: translateY(-100%);
        }

        @keyframes rightAnimation {
          0% { transform: translateY(-100%); }
          3% { transform: translateY(-50%); }
          12% { transform: translateY(-50%); }
          18% { transform: translateY(-55%); }
          25% { transform: translateY(0%); }
          28% { transform: translateY(0%); }
          35% { transform: translateY(60%); }
          38% { transform: translateY(60%); }
          50% { transform: translateY(100%); }
          100% { transform: translateY(100%); }
        }

        span.bottom {
          bottom: 0;
          left: 0;
          width: 100%;
          height: 2px;
          animation: bottomAnimation 4s linear infinite;
          animation-delay: 2s;
          transform: translateX(-100%);
        }

        @keyframes bottomAnimation {
          0% { transform: translateX(-100%); }
          3% { transform: translateX(-70%); }
          12% { transform: translateX(-70%); }
          15% { transform: translateX(-75%); }
          24% { transform: translateX(-75%); }
          28% { transform: translateX(-100%); }
          100% { transform: translateX(-100%); }
        }

        span.left {
          top: 0;
          left: 0;
          width: 2px;
          height: 100%;
          animation: leftAnimation 4s linear infinite;
          animation-delay: 2s;
          transform: translateY(100%);
        }

        @keyframes leftAnimation {
          0% { transform: translateY(100%); }
          3% { transform: translateY(50%); }
          12% { transform: translateY(50%); }
          18% { transform: translateY(55%); }
          25% { transform: translateY(0%); }
          28% { transform: translateY(0%); }
          35% { transform: translateY(-60%); }
          38% { transform: translateY(-60%); }
          50% { transform: translateY(-100%); }
          100% { transform: translateY(-100%); }
        }

        /* Mimicking ::before */
        .before {
          content: "";
          position: absolute;
          top: 0;
          left: 0;
          width: 100%;
          height: 2px;
          background-color: var(--gold-gradient);
          animation: cornerHelperTopAnimation 4s linear infinite;
          animation-delay: 2s;
          transform: translateX(-100%);
        }

        @keyframes cornerHelperTopAnimation {
          0% { transform: translateX(-100%); }
          22% { transform: translateX(-100%); }
          25% { transform: translateX(-70%); }
          35% { transform: translateX(-85%); }
          38% { transform: translateX(-85%); }
          50% { transform: translateX(-100%); }
          100% { transform: translateX(-100%); }
        }

        /* Mimicking ::after */
        .after {
          content: "";
          position: absolute;
          bottom: 0;
          left: 0;
          width: 100%;
          height: 2px;
          background-color: var(--gold-gradient);
          animation: cornerHelperBottomAnimation 4s linear infinite;
          transform: translateX(100%);
        }

        @keyframes cornerHelperBottomAnimation {
          0% { transform: translateX(100%); }
          22% { transform: translateX(100%); }
          25% { transform: translateX(70%); }
          35% { transform: translateX(85%); }
          38% { transform: translateX(85%); }
          50% { transform: translateX(100%); }
          100% { transform: translateX(100%); }
        }

        .text {
          position: relative;
          z-index: 1;
        }
      </style>
      <span class="before"></span>
      <span class="top"></span>
      <span class="right"></span>
      <span class="bottom"></span>
      <span class="left"></span>
      <span class="after"></span>
      <slot></slot>
    `;
  }
}

customElements.define('snake-border-button', SnakeBorderButton);


// Custom Element to Prevent Child Dropdown if one is open and another will close
class SubMenuDrawer extends HTMLElement {
  constructor() {
    super();
  }

  connectedCallback() {
    this.init();
  }

  init() {
    this.querySelectorAll(".sub-menu-drawer-inner").forEach((submenu) => {
      submenu.querySelectorAll("details").forEach((details) => {
        const summary = details.querySelector("summary");
        const content = details.querySelector(".details-content");

        if (!summary || !content) return;

        // On click, animate open/close
        summary.addEventListener("click", (e) => {
          e.preventDefault();

          const isOpen = details.hasAttribute("open");

          if (isOpen) {
            this.animateClose(details, content);
          } else {
            // Close siblings
            submenu.querySelectorAll("details[open]").forEach((openDetails) => {
              if (openDetails !== details) {
                const openContent = openDetails.querySelector(".details-content");
                this.animateClose(openDetails, openContent);
              }
            });

            this.animateOpen(details, content);
          }
        });

        // Force initial state
        if (!details.hasAttribute("open")) {
          content.style.maxHeight = "0";
          content.style.opacity = "0";
        }
      });
    });
  }

  animateOpen(details, content) {
    details.setAttribute("open", true);

    // Wait for DOM to update
    requestAnimationFrame(() => {
      const height = content.scrollHeight + "px";
      content.style.maxHeight = height;
      content.style.opacity = "1";
    });
  }

  animateClose(details, content) {
    const height = content.scrollHeight + "px";
    content.style.maxHeight = height;

    // Required to trigger transition from current height
    requestAnimationFrame(() => {
      content.style.maxHeight = "0";
      content.style.opacity = "0";

      content.addEventListener(
        "transitionend",
        function handler(e) {
          if (e.propertyName === "max-height") {
            details.removeAttribute("open");
            content.removeEventListener("transitionend", handler);
          }
        },
        { once: true }
      );
    });
  }
}

customElements.define("sub-menu-drawer", SubMenuDrawer);


// Validating footer newsletter for email and consent
  document.addEventListener("DOMContentLoaded", () => {
    const form = document.querySelector('.newsletter-form');
    const submitBtn = document.getElementById('Subscribe');
    const errorMsg = document.getElementById('error-msg');
    const errorDiv = document.getElementsByClassName('newsletter-form__message');
  
    function isValidEmail(email) {
      const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
      return emailRegex.test(email);
    }
  
    form.addEventListener('submit', (event) => {
      event.preventDefault();
  
      const emailInput = form.querySelector('input[type="email"]');
      const email = emailInput.value.trim();
  
      if (isValidEmail(email)) {
        form.submit();
      } else {
        errorMsg.textContent = 'Please enter a valid email address.';
        errorMsg.style.display = 'inline-flex';
        for (let i = 0; i < errorDiv.length; i++) {
          errorDiv[i].style.display = 'none';
        }
      }
    });
  
    submitBtn.addEventListener('click', (event) => {
      const emailInput = form.querySelector('input[type="email"]');
      const email = emailInput.value.trim();
      const checkbox = document.getElementById("consent-checkbox");
      const consentErrorElement = document.getElementById('error-msg-consent');
  
      if (!isValidEmail(email) && !checkbox.checked) {
        event.preventDefault();
        errorMsg.style.display = 'inline-flex';
        for (let i = 0; i < errorDiv.length; i++) {
          errorDiv[i].style.display = 'none';
        }
        consentErrorElement.style.display = 'block';
      } else if(!isValidEmail(email) && checkbox.checked) {
        event.preventDefault();
        errorMsg.style.display = 'inline-flex';
        consentErrorElement.style.display= 'none';
        for (let i = 0; i < errorDiv.length; i++) {
          errorDiv[i].style.display = 'none';
        }
      } else if(isValidEmail(email) && !checkbox.checked) {
        event.preventDefault();
        errorMsg.style.display = 'none';
        consentErrorElement.style.display = 'block';
      } else {  
        errorMsg.style.display = 'none';
        consentErrorElement.style.display= 'none';
      }
    });
  });

class ProductCard extends HTMLElement {
  constructor() {
    super();

    this.productHandle = this.dataset.productHandle;
    this.sectionId = this.dataset.sectionId;

    this.variantData = JSON.parse(this.querySelector('script').textContent);
    this.addEventListener('change', this.onOptionChange);
  }

  onOptionChange() {
    this.selectedOptions = Array.from(this.querySelectorAll("select"), select => select.value);
    this.currentVariant = this.variantData.find(item => JSON.stringify(item.options) === JSON.stringify(this.selectedOptions));
    this.getUpdatedCard();
  }

  getUpdatedCard() {
    const url = `/products/${this.productHandle}?view=custom-product-card-render&variant=${this.currentVariant.id}`;
    fetch(url)
      .then((response) => response.text())
      .then((responseText) => {
        const html = new DOMParser().parseFromString(responseText, "text/html");
        this.innerHTML = html.querySelector(
          `[data-product-handle="${this.productHandle}"]`
        ).innerHTML;
        // code to change the +2 Freebies tag to free gift for 30ml variant
        if(this.currentVariant.id == 47702079635698) {
          const badgeHeading = this.querySelector('.product-card__badge-heading');
          const badges = this.querySelector('.product-card__badges');
          if(badgeHeading) badgeHeading.style.display="none";
          if(badges) badges.style.display="none";
        } else {
          const badgeHeading = this.querySelector('.product-card__badge-heading');
          const badges = this.querySelector('.product-card__badges');
          if(badgeHeading) {
            badgeHeading.innerHTML = "Free Gift"; 
            badgeHeading.style.display = "block";
          }
          if(badges) {
            badges.style.display = "block";
          }
        }
        if (window.Shopify && Shopify.bind) {
          Shopify.bind();
        }
        let yotpoStarWidget = this.querySelector('.yotpo-widget-instance');
        if(yotpoStarWidget || this.currentVariant.id == 47702079635698 || this.currentVariant.id == 47702079602930 ){
          yotpoStarWidget.innerHTML=5
        }
      });
  }
}

customElements.define('product-card', ProductCard);


// Custom JS for Caution Notice Dropdown Functionality

document.addEventListener("DOMContentLoaded", function () {
  const details = document.getElementById("noticeDetails");
  if (!details) return;

  const summary = details.querySelector("summary");
  const caretIcon = details.querySelector(".caret-icon");
  const content = details.querySelector(".Notice-container");

  if (!summary || !caretIcon || !content) return;

  // Set initial styles
  content.style.maxHeight = details.open ? content.scrollHeight + "px" : "0px";
  content.style.opacity = details.open ? "1" : "0";
  caretIcon.style.transform = details.open ? "rotate(180deg)" : "rotate(0deg)";

  summary.addEventListener("click", function (e) {
    e.preventDefault(); // Prevent default toggle behavior so we control it manually

    if (details.open) {
      // Closing animation
      content.style.maxHeight = "0px";
      content.style.opacity = "0";
      caretIcon.style.transform = "rotate(0deg)";
      setTimeout(() => {
        details.removeAttribute("open");
      }, 400); // Delay until after animation
    } else {
      // Opening animation
      details.setAttribute("open", "true");
      content.style.maxHeight = content.scrollHeight + "px";
      content.style.opacity = "1";
      caretIcon.style.transform = "rotate(180deg)";

      setTimeout(() => {
        details.scrollIntoView({ behavior: "smooth", block: "start" });
      }, 420);
    }
  });
});

// Custom Validation For Name and Phone Number in Contact US Page

document.addEventListener('DOMContentLoaded', function () {
  const form = document.getElementById('ContactForm');
  const nameInput = document.getElementById('ContactForm-name');
  const phoneInput = document.getElementById('ContactForm-phone');
  const consentInput = document.getElementById('consent');
  const submitButton = form?.querySelector('button[type="submit"]');

  nameInput?.addEventListener('input', function () {
    this.value = this.value.replace(/[^a-zA-Z\s]/g, '');
    validateField(nameInput);
  });

  phoneInput?.addEventListener('input', function () {
    let val = this.value;
    // Remove non-digit characters
    val = val.replace(/\D/g, '');
    // Limit to 10 digits
    if (val.length > 10) {
      val = val.slice(0, 10);
    }
    this.value = val;
    // Validate number: 10 digits starting with 6-9
    if (/^[6-9]\d{9}$/.test(val)) {
      this.setCustomValidity(''); // Valid
    } else {
      this.setCustomValidity('Enter a valid 10-digit mobile number');
    }
  });

  phoneInput?.addEventListener('input', validateField);

  function validateField() {
    clearError(nameInput);
    clearError(phoneInput);

    const phoneValue = phoneInput.value.replace(/\D/g, '');

    if (nameInput.value.trim().length < 3) {
      showError(nameInput, 'Minimum length for name should be 3 letters.');
    }

    if (phoneValue.length < 10) {
      showError(phoneInput, 'Phone number must be at least 10 digits.');
    } else if (phoneValue.length > 10) {
      showError(phoneInput, 'Phone number must be exactly 10 digits.');
    }

    const formIsValid = checkFormValidity();
    if (submitButton) {
      submitButton.disabled = !formIsValid;
    }
  }

  function checkFormValidity() {
    const nameValid = nameInput.value.trim().length >= 3;
    const phoneValue = phoneInput.value.replace(/\D/g, '');
    const phoneValid = phoneValue.length === 10;
    return nameValid && phoneValid;
  }

  function showError(inputElement, message) {
    const errorContainer = inputElement.parentNode.querySelector('.contact__field-error');
    if (errorContainer) {
      const messageSpan = errorContainer.querySelector('.error-text');
      if (messageSpan) {
        messageSpan.innerText = message;
      }
      errorContainer.style.display = 'block';
    }
  }

  function clearError(inputElement) {
    const errorContainer = inputElement.parentNode.querySelector('.contact__field-error');
    if (errorContainer) {
      const messageSpan = errorContainer.querySelector('.error-text');
      if (messageSpan) {
        messageSpan.innerText = '';
      }
      errorContainer.style.display = 'none';
    }
  }

  form?.addEventListener('submit', function (event) {
    clearError(nameInput);
    clearError(phoneInput);

    const nameValid = nameInput.value.trim().length >= 3;
    const phoneValue = phoneInput.value.replace(/\D/g, '');
    const phoneValid = phoneValue.length === 10;
    // const consentChecked = consentInput.checked;

    if (!nameValid) {
      showError(nameInput, 'Minimum length for name should be 3 letters.');
    }

    if (!phoneValid) {
      showError(phoneInput, phoneValue.length < 10
        ? 'Phone number must be at least 10 digits.'
        : 'Phone number must be exactly 10 digits.');
    }

    if (!(nameValid && phoneValid)) {
      event.preventDefault();
    }
  });

  // Initial check on load in case of autofill
  // validateField();
});


// Custom Validation For Name and Phone Number in Create Account Page

document.addEventListener('DOMContentLoaded', function () {
  const firstNameInput = document.getElementById('RegisterForm-FirstName');
  const errorMsg = document.getElementById('firstNameError');
  const submitButton = document.getElementById('registerSubmit');

  function validateFirstName() {
    const value = firstNameInput.value.trim();
    const isValid = /^[A-Za-z]{3,}$/.test(value);

    if (!isValid) {
      errorMsg.style.display = 'flex';
      submitButton.disabled = true;
    } else {
      errorMsg.style.display = 'none';
      submitButton.disabled = false;
    }
  }

  // Show validation when field is focused or changed
  if(firstNameInput) {
    firstNameInput.addEventListener('focus', validateFirstName);
    firstNameInput.addEventListener('input', validateFirstName);
  }

  // Disable button initially
  if(submitButton){
    submitButton.disabled = true;
  }
});


// Hide and Show Button For Password

document.querySelectorAll('.toggle-password-btn').forEach(button => {
  const field = button.closest('.password-field');
  const input = field.querySelector('input[type="password"], input[type="text"]');
  const slashPath = button.querySelector('.eye-slash');

  let isVisible = false;

  button.addEventListener('click', () => {
    isVisible = !isVisible;
    input.type = isVisible ? 'text' : 'password';

    if (slashPath) {
      slashPath.style.opacity = isVisible ? '1' : '0';
    }
  });
});


// Custom JS for PDP Collapsible Content Dropdown 

document.addEventListener('DOMContentLoaded', () => {
  const detailsElements = document.querySelectorAll('details.details-accordion');

  detailsElements.forEach(details => {
    const summary = details.querySelector('summary');
    const content = details.querySelector('.accordion__content');

    summary.addEventListener('click', (e) => {

      if (details.hasAttribute('open')) {
        e.preventDefault();

        content.style.maxHeight = content.scrollHeight + 'px';
        content.style.opacity = '1';

        requestAnimationFrame(() => {
          content.style.maxHeight = '0';
          content.style.opacity = '0';
        });

        // Remove open attribute after transition ends
        setTimeout(() => {
          details.removeAttribute('open');
        }, 400);
      } else {
        // Let browser handle native open — then animate in
        // Wait a bit for [open] to be applied
        setTimeout(() => {
          content.style.maxHeight = content.scrollHeight + 'px';
          content.style.opacity = '1';
        }, 10);
      }
    });
  });
});

// Custom JS for Gift Card Accordian
document.addEventListener('DOMContentLoaded', () => {
  const detailsElements = document.querySelectorAll('details.details-accordion');

  const targetProductHandle = 'gift-card';
  
  detailsElements.forEach(details => {
    const summary = details.querySelector('summary');
    const content = details.querySelector('.accordion__content');
    
    if (details.dataset.productHandle === targetProductHandle) {
      // Open this accordion by default
      details.setAttribute('open', '');
      content.style.maxHeight = content.scrollHeight + 'px';
      content.style.opacity = '1';
    }

    summary.addEventListener('click', (e) => {
      if (details.hasAttribute('open')) {
        e.preventDefault();
        content.style.maxHeight = content.scrollHeight + 'px';
        content.style.opacity = '1';

        requestAnimationFrame(() => {
          content.style.maxHeight = '0';
          content.style.opacity = '0';
        });

        setTimeout(() => {
          details.removeAttribute('open');
        }, 400);
      } else {
        setTimeout(() => {
          content.style.maxHeight = content.scrollHeight + 'px';
          content.style.opacity = '1';
        }, 10);
      }
    });
  });
});

// Custom JS for PDP FAQ Section

document.addEventListener("DOMContentLoaded", () => {
  const faqs = document.querySelectorAll("details.product-faqs");

  faqs.forEach((faq) => {
    const summary = faq.querySelector("summary");
    const answer = faq.querySelector(".faq-answer-container");

    summary.addEventListener("click", (e) => {
      if (faq.hasAttribute("open")) {
        e.preventDefault();

        answer.style.maxHeight = answer.scrollHeight + "px";
        answer.style.opacity = "1";

        requestAnimationFrame(() => {
          answer.style.maxHeight = "0";
          answer.style.opacity = "0";
        });

        // Remove [open] after transition ends
        setTimeout(() => {
          faq.removeAttribute("open");
        }, 400); // Match transition duration
      } else {
        // Allow native open, then animate
        setTimeout(() => {
          answer.style.maxHeight = answer.scrollHeight + "px";
          answer.style.opacity = "1";
        }, 10);
      }
    });
  });
});


// custom js for write a review 

document.addEventListener("DOMContentLoaded", function () {

  function applyStyling() {
    try {
      let applied = false;

      const ALL_SVG_PATHS = document.querySelectorAll(
        'svg.yotpo-sr-star-full path[fill*="yotpo_stars_gradient_"], svg.yotpo-star-rating-icon path[fill*="yotpo_stars_gradient_"]'
      );

      ALL_SVG_PATHS.forEach(path => {
        const fill = path.getAttribute('fill');
        if (!fill) return;

        // const match = fill.match(/url\((?:['"])?#?(yotpo_stars_gradient_[^)'" ]+)(?:['"])?\)/);
        const match = fill.match(/url\(#(yotpo_stars_gradient_[^)]+)\)/);
        if (!match) return;

        const gradientId = match[1];
        const gradient = document.querySelector(`linearGradient#${CSS.escape(gradientId)}`);
        if (!gradient) return;

        const svg = path.closest('svg');
        if (!svg) return;

        const viewBox = svg.getAttribute('viewBox');

        // Handle only these two cases
        const is18x18 = viewBox === '0 0 18 18';
        const is33x33 = viewBox === '0 0 33 33';

        // If 33x33, check if the gradient contains a gold/yellow stop
        if (is33x33) {
          const stops = gradient.querySelectorAll('stop');
          const hasGoldStop = Array.from(stops).some(stop =>
            stop.getAttribute('stop-color')?.trim() === 'rgba(255,215,0,1)'
          );
          if (!hasGoldStop) return; // Skip if no gold/yellow stop
        } else if (!is18x18) {
          return; // Skip anything that is not 18x18 or qualifying 33x33
        }

        // Apply fill and stroke
        path.setAttribute('fill', `url(#${gradientId})`);
        path.setAttribute('stroke', '#ffffff');

        // Overwrite gradient
        gradient.innerHTML = `
          <stop offset="-7.35%" stop-color="#a8875d" />
          <stop offset="14.38%" stop-color="#d6b890" />
          <stop offset="25%" stop-color="#ead5ad" />
          <stop offset="38.77%" stop-color="#ffeac6" />
          <stop offset="53.09%" stop-color="#d7b883" />
          <stop offset="70%" stop-color="#ad833b" />
          <stop offset="82.78%" stop-color="#cfad76" />
          <stop offset="98.68%" stop-color="#e3cd9d" />
        `;
        applied = true;
      });

      // Style half stars
      document.querySelectorAll('svg.yotpo-sr-star-half defs linearGradient').forEach(grad => {
        grad.innerHTML = `
          <stop offset="0%" stop-color="#a8875d" />
          <stop offset="40%" stop-color="#e3cd9d" />
          <stop offset="50%" stop-color="transparent" />
          <stop offset="100%" stop-color="transparent" />
        `;
      });

      // Style empty stars
      document.querySelectorAll('svg.yotpo-sr-star-empty defs linearGradient stop').forEach(stop => {
        stop.setAttribute('stop-color', 'black');
      });

      // styles for stars stroke i.e, border
      document.querySelectorAll('svg.yotpo-star-rating-icon').forEach(svg => {
        const gradientId = `yotpo_stroke_gradient_${Math.random().toString().slice(2)}`;

        // Create <defs> if not present
        let defs = svg.querySelector('defs');
        if (!defs) {
          defs = document.createElementNS("http://www.w3.org/2000/svg", "defs");
          svg.prepend(defs);
        }

        // Create <linearGradient>
        const gradient = document.createElementNS("http://www.w3.org/2000/svg", "linearGradient");
        gradient.setAttribute("id", gradientId);
        gradient.setAttribute("x1", "0%");
        gradient.setAttribute("y1", "0%");
        gradient.setAttribute("x2", "100%");
        gradient.setAttribute("y2", "0%");

        // Create gradient stops
        const stop1 = document.createElementNS("http://www.w3.org/2000/svg", "stop");
        stop1.setAttribute("offset", "0%");
        stop1.setAttribute("stop-color", "#a8875d");

        const stop2 = document.createElementNS("http://www.w3.org/2000/svg", "stop");
        stop2.setAttribute("offset", "100%");
        stop2.setAttribute("stop-color", "#e3cd9d");

        // Append stops to gradient
        gradient.appendChild(stop1);
        gradient.appendChild(stop2);
        defs.appendChild(gradient);

        // Apply gradient to <path stroke="">
        const path = svg.querySelector('path');
        if (path) {
          path.setAttribute('stroke', `url(#${gradientId})`);
        }
      });

      // styling the stars inside product__info-wrapper
      const wrapper = document.querySelector('.product__info-wrapper');
      if (!wrapper) return;

      // Handle FULL stars
      const fullStars = wrapper.querySelectorAll('.yotpo-sr-star-full');
      fullStars.forEach(svg => updateGradient(svg, 'full'));

      // Handle HALF stars
      const halfStars = wrapper.querySelectorAll('.yotpo-sr-star-half');
      halfStars.forEach(svg => updateGradient(svg, 'half'));

      function updateGradient(svg, type) {
        const path = svg.querySelector('path');
        if (!path) return;

        const fillAttr = path.getAttribute('fill');
        const gradientIdMatch = fillAttr?.match(/url\(['"]?#(.*?)['"]?\)/);
        if (!gradientIdMatch) return;

        const gradientId = gradientIdMatch[1];
        const gradient = svg.querySelector(`#${CSS.escape(gradientId)}`);
        if (!gradient) return;

        // Clear existing stops
        gradient.innerHTML = '';

        // Apply correct gradient stops
        if (type === 'full') {
          gradient.appendChild(createStop('0%', '#a8875d'));
          gradient.appendChild(createStop('100%', '#e3cd9d'));
        } else if (type === 'half') {
          gradient.appendChild(createStop('0%', '#a8875d'));
          gradient.appendChild(createStop('40%', '#e3cd9d'));
          gradient.appendChild(createStop('50%', 'transparent'));
          gradient.appendChild(createStop('100%', 'transparent'));
        }

      // end of styling the stars inside product__info-wrapper

        // Ensure white stroke
        path.setAttribute('stroke', 'rgb(168,135,93)');
      }

      function createStop(offset, color) {
        const stop = document.createElementNS('http://www.w3.org/2000/svg', 'stop');
        stop.setAttribute('offset', offset);
        stop.setAttribute('stop-color', color);
        return stop;
      }

      return applied;
    } catch (e) {
      console.error('Yotpo gradient styling error:', e);
      return false;
    }
  }

  // Polling approach with reasonable limits
  function waitForStarsAndStyle() {
    let attempts = 0;
    const maxAttempts = 10; // Maximum 5 seconds (500ms * 10)
    
    const checkAndStyle = () => {
      attempts++;
      
      if (applyStyling()) {
        // Successfully applied styling
        return;
      }
      
      if (attempts < maxAttempts) {
        setTimeout(checkAndStyle, 500); // Check every 500ms
      } else {
        console.warn('Could not style Yotpo stars after maximum attempts');
      }
    };
    
    checkAndStyle();
  }

  waitForStarsAndStyle();

  function appendReviewLink() {
    const yotpoWidget = document.querySelector('.product__info-container .yotpo-reviews-star-ratings-widget');

    if (yotpoWidget) {
      // Avoid duplicate link
      if (!document.querySelector('#review_yotpo_popup')) {
        const link = document.createElement('a');
        link.href = '#';
        link.id = 'review_yotpo_popup';
        link.textContent = 'Write A Review';

        // Style it like a button with white text
        link.style.display = 'inline-block';
        link.style.padding = '4px 15px';
        link.style.backgroundColor = '#000';
        link.style.color = '#fff';
        link.style.fontSize = '12px';
        link.style.textDecoration = 'none';
        link.style.borderRadius = '4px';
        link.style.fontWeight = 'bold';
        link.style.textDecoration = 'underline';
        link.style.textUnderlineOffset = '1px';

        yotpoWidget.appendChild(link);
        yotpoWidget.appendChild(link);
      }
    } else {
      setTimeout(appendReviewLink, 1000); // Retry after 1s
    }
  }

  appendReviewLink();
  
  // commenting this function because in requirement it was said not to show anything if no reviews
  // function appendWidgetforReview() {
  //   const reviewWidgetHead = document.querySelector('.yotpo-head');
  //   if(reviewWidgetHead){
  //     const emptyReviewContainer = document.querySelector('.yotpo-empty-state');
  //     if(emptyReviewContainer) {
  //       if (!document.querySelector('.yotpo-header-container')) {
  //         const widget = `
  //         <div data-v-012a3912="" class="yotpo-header-container custom-mobile-zero-review" style="--2fb9a698: rgb(46, 79, 124, 0.05);">
  //           <div data-v-012a3912="" class="yotpo-layout-header-wrapper">
  //               <div data-v-012a3912="" class="yotpo-bottom-line" average-score="5">
  //                 <!---->
  //                 <div class="yotpo-summary-section">
  //                     <div class="yotpo-bottom-line-summary">
  //                       <div data-v-8b122940="" class="yotpo-bottom-line-left-panel yotpo-bottom-line-score" aria-hidden="true">0</div>
  //                       <div class="yotpo-bottom-line-right-panel">
  //                           <div class="yotpo-star-rating yotpo-bottom-line-stars">
  //                             <div class="star-container">
  //                                 <svg class="yotpo-star-rating-icon yotpo-sr-star-full" width="18" height="18" viewBox="0 0 33 33" fill="none" xmlns="http://www.w3.org/2000/svg" transform="scale(1.2, 1.2)">
  //                                   <defs>
  //                                       <linearGradient id="yotpo_stars_gradient_0.7038924170955959">
  //                                         <stop offset="100%" stop-color="rgb(0, 0, 0)"></stop>
  //                                         <stop offset="100%" stop-color="#FFFFFF" stop-opacity="1"></stop>
  //                                       </linearGradient>
  //                                   </defs>
  //                                   <path d="M17.0919 25.4549L16.8335 25.299L16.5751 25.4549L7.39263 30.9971L9.82942 20.5516L9.89798 20.2577L9.66988 20.0601L1.55658 13.0315L12.2393 12.1252L12.5397 12.0997L12.6574 11.8221L16.8335 1.9688L21.0096 11.8221L21.1273 12.0997L21.4277 12.1252L32.1104 13.0315L23.9971 20.0601L23.769 20.2577L23.8376 20.5516L26.2744 30.9971L17.0919 25.4549Z" stroke="rgba(255,215,0,1)" fill="url(#yotpo_stars_gradient_0.7038924170955959)"></path>
  //                                 </svg>
  //                             </div>
  //                             <div class="star-container">
  //                                 <svg class="yotpo-star-rating-icon yotpo-sr-star-full" width="18" height="18" viewBox="0 0 33 33" fill="none" xmlns="http://www.w3.org/2000/svg" transform="scale(1.2, 1.2)">
  //                                   <defs>
  //                                       <linearGradient id="yotpo_stars_gradient_0.04107536169853909">
  //                                         <stop offset="100%" stop-color="rgb(0, 0, 0)"></stop>
  //                                         <stop offset="100%" stop-color="#FFFFFF" stop-opacity="1"></stop>
  //                                       </linearGradient>
  //                                   </defs>
  //                                   <path d="M17.0919 25.4549L16.8335 25.299L16.5751 25.4549L7.39263 30.9971L9.82942 20.5516L9.89798 20.2577L9.66988 20.0601L1.55658 13.0315L12.2393 12.1252L12.5397 12.0997L12.6574 11.8221L16.8335 1.9688L21.0096 11.8221L21.1273 12.0997L21.4277 12.1252L32.1104 13.0315L23.9971 20.0601L23.769 20.2577L23.8376 20.5516L26.2744 30.9971L17.0919 25.4549Z" stroke="rgba(255,215,0,1)" fill="url(#yotpo_stars_gradient_0.04107536169853909)"></path>
  //                                 </svg>
  //                             </div>
  //                             <div class="star-container">
  //                                 <svg class="yotpo-star-rating-icon yotpo-sr-star-full" width="18" height="18" viewBox="0 0 33 33" fill="none" xmlns="http://www.w3.org/2000/svg" transform="scale(1.2, 1.2)">
  //                                   <defs>
  //                                       <linearGradient id="yotpo_stars_gradient_0.42142436901401825">
  //                                         <stop offset="100%" stop-color="rgb(0, 0, 0)"></stop>
  //                                         <stop offset="100%" stop-color="#FFFFFF" stop-opacity="1"></stop>
  //                                       </linearGradient>
  //                                   </defs>
  //                                   <path d="M17.0919 25.4549L16.8335 25.299L16.5751 25.4549L7.39263 30.9971L9.82942 20.5516L9.89798 20.2577L9.66988 20.0601L1.55658 13.0315L12.2393 12.1252L12.5397 12.0997L12.6574 11.8221L16.8335 1.9688L21.0096 11.8221L21.1273 12.0997L21.4277 12.1252L32.1104 13.0315L23.9971 20.0601L23.769 20.2577L23.8376 20.5516L26.2744 30.9971L17.0919 25.4549Z" stroke="rgba(255,215,0,1)" fill="url(#yotpo_stars_gradient_0.42142436901401825)"></path>
  //                                 </svg>
  //                             </div>
  //                             <div class="star-container">
  //                                 <svg class="yotpo-star-rating-icon yotpo-sr-star-full" width="18" height="18" viewBox="0 0 33 33" fill="none" xmlns="http://www.w3.org/2000/svg" transform="scale(1.2, 1.2)">
  //                                   <defs>
  //                                       <linearGradient id="yotpo_stars_gradient_0.11683207588994404">
  //                                         <stop offset="100%" stop-color="rgb(0, 0, 0)"></stop>
  //                                         <stop offset="100%" stop-color="#FFFFFF" stop-opacity="1"></stop>
  //                                       </linearGradient>
  //                                   </defs>
  //                                   <path d="M17.0919 25.4549L16.8335 25.299L16.5751 25.4549L7.39263 30.9971L9.82942 20.5516L9.89798 20.2577L9.66988 20.0601L1.55658 13.0315L12.2393 12.1252L12.5397 12.0997L12.6574 11.8221L16.8335 1.9688L21.0096 11.8221L21.1273 12.0997L21.4277 12.1252L32.1104 13.0315L23.9971 20.0601L23.769 20.2577L23.8376 20.5516L26.2744 30.9971L17.0919 25.4549Z" stroke="rgba(255,215,0,1)" fill="url(#yotpo_stars_gradient_0.11683207588994404)"></path>
  //                                 </svg>
  //                             </div>
  //                             <div class="star-container">
  //                                 <svg class="yotpo-star-rating-icon yotpo-sr-star-full" width="18" height="18" viewBox="0 0 33 33" fill="none" xmlns="http://www.w3.org/2000/svg" transform="scale(1.2, 1.2)">
  //                                   <defs>
  //                                       <linearGradient id="yotpo_stars_gradient_0.17283503441298997">
  //                                         <stop offset="100%" stop-color="rgb(0, 0, 0)"></stop>
  //                                         <stop offset="100%" stop-color="#FFFFFF" stop-opacity="1"></stop>
  //                                       </linearGradient>
  //                                   </defs>
  //                                   <path d="M17.0919 25.4549L16.8335 25.299L16.5751 25.4549L7.39263 30.9971L9.82942 20.5516L9.89798 20.2577L9.66988 20.0601L1.55658 13.0315L12.2393 12.1252L12.5397 12.0997L12.6574 11.8221L16.8335 1.9688L21.0096 11.8221L21.1273 12.0997L21.4277 12.1252L32.1104 13.0315L23.9971 20.0601L23.769 20.2577L23.8376 20.5516L26.2744 30.9971L17.0919 25.4549Z" stroke="rgba(255,215,0,1)" fill="url(#yotpo_stars_gradient_0.17283503441298997)"></path>
  //                                 </svg>
  //                             </div>
  //                           </div>
  //                           <div class="yotpo-bottom-line-text" style="color: rgb(255, 255, 255); font-family: &quot;Nunito Sans&quot;; font-weight: 400; font-style: normal;">
  //                             <span class="sr-only">5 out of 5 stars 6 total reviews</span>
  //                             <div class="yotpo-bottom-line-basic-text" aria-hidden="true">No reviews yet</div>
  //                             <!---->
  //                           </div>
  //                       </div>
  //                       <!---->
  //                     </div>
  //                     <!---->
  //                 </div>
  //                 <div class="yotpo-vertical-border"></div>
  //                 <div class="yotpo-star-distribution" style="border-right: 1px solid var(--yotpo-separator-line-grey); padding-bottom: 20px;">
  //                     <div class="yotpo-star-distribution-row-wrapper default-row yotpo-distribution-row" aria-label="5 star by 6 reviews" role="button" tabindex="0">
  //                       <div class="yotpo-star-distribution-index-wrapper">
  //                           <div class="yotpo-star-distribution-index" aria-hidden="true">5</div>
  //                           <svg class="yotpo-star-rating-icon yotpo-sr-star-full" viewBox="0 0 18 18" xmlns="http://www.w3.org/2000/svg" transform="scale(1.2, 1.2)" style="width: 14px; height: 14px;">
  //                             <defs>
  //                                 <linearGradient id="yotpo_stars_gradient_0.2272286640265614">
  //                                   <stop offset="100%" stop-color="rgba(255,215,0,1)"></stop>
  //                                   <stop offset="100%" stop-color="#eaeaea" stop-opacity="1"></stop>
  //                                 </linearGradient>
  //                             </defs>
  //                             <path d="M9 14.118L14.562 17.475L13.086 11.148L18 6.891L11.529 6.342L9 0.375L6.471 6.342L0 6.891L4.914 11.148L3.438 17.475L9 14.118Z" fill="url(#yotpo_stars_gradient_0.2272286640265614)"></path>
  //                           </svg>
  //                       </div>
  //                       <div class="yotpo-star-distribution-bar">
  //                           <div class="yotpo-progress-bar">
  //                             <div class="yotpo-progress-bar-score" style="width: 0%;"></div>
  //                           </div>
  //                           <!---->
  //                       </div>
  //                       <div class="yotpo-star-distribution-value" aria-hidden="true">0</div>
  //                     </div>
  //                     <div class="yotpo-star-distribution-row-wrapper default-row yotpo-distribution-row" aria-label="4 star by 0 reviews" role="button" tabindex="0">
  //                       <div class="yotpo-star-distribution-index-wrapper">
  //                           <div class="yotpo-star-distribution-index" aria-hidden="true">4</div>
  //                           <svg class="yotpo-star-rating-icon yotpo-sr-star-full" viewBox="0 0 18 18" xmlns="http://www.w3.org/2000/svg" transform="scale(1.2, 1.2)" style="width: 14px; height: 14px;">
  //                             <defs>
  //                                 <linearGradient id="yotpo_stars_gradient_0.1364277586062682">
  //                                   <stop offset="100%" stop-color="rgba(255,215,0,1)"></stop>
  //                                   <stop offset="100%" stop-color="#eaeaea" stop-opacity="1"></stop>
  //                                 </linearGradient>
  //                             </defs>
  //                             <path d="M9 14.118L14.562 17.475L13.086 11.148L18 6.891L11.529 6.342L9 0.375L6.471 6.342L0 6.891L4.914 11.148L3.438 17.475L9 14.118Z" fill="url(#yotpo_stars_gradient_0.1364277586062682)"></path>
  //                           </svg>
  //                       </div>
  //                       <div class="yotpo-star-distribution-bar">
  //                           <div class="yotpo-progress-bar">
  //                             <div class="yotpo-progress-bar-score" style="width: 0%;"></div>
  //                           </div>
  //                           <!---->
  //                       </div>
  //                       <div class="yotpo-star-distribution-value" aria-hidden="true">0</div>
  //                     </div>
  //                     <div class="yotpo-star-distribution-row-wrapper default-row yotpo-distribution-row" aria-label="3 star by 0 reviews" role="button" tabindex="0">
  //                       <div class="yotpo-star-distribution-index-wrapper">
  //                           <div class="yotpo-star-distribution-index" aria-hidden="true">3</div>
  //                           <svg class="yotpo-star-rating-icon yotpo-sr-star-full" viewBox="0 0 18 18" xmlns="http://www.w3.org/2000/svg" transform="scale(1.2, 1.2)" style="width: 14px; height: 14px;">
  //                             <defs>
  //                                 <linearGradient id="yotpo_stars_gradient_0.5212165851430284">
  //                                   <stop offset="100%" stop-color="rgba(255,215,0,1)"></stop>
  //                                   <stop offset="100%" stop-color="#eaeaea" stop-opacity="1"></stop>
  //                                 </linearGradient>
  //                             </defs>
  //                             <path d="M9 14.118L14.562 17.475L13.086 11.148L18 6.891L11.529 6.342L9 0.375L6.471 6.342L0 6.891L4.914 11.148L3.438 17.475L9 14.118Z" fill="url(#yotpo_stars_gradient_0.5212165851430284)"></path>
  //                           </svg>
  //                       </div>
  //                       <div class="yotpo-star-distribution-bar">
  //                           <div class="yotpo-progress-bar">
  //                             <div class="yotpo-progress-bar-score" style="width: 0%;"></div>
  //                           </div>
  //                           <!---->
  //                       </div>
  //                       <div class="yotpo-star-distribution-value" aria-hidden="true">0</div>
  //                     </div>
  //                     <div class="yotpo-star-distribution-row-wrapper default-row yotpo-distribution-row" aria-label="2 star by 0 reviews" role="button" tabindex="0">
  //                       <div class="yotpo-star-distribution-index-wrapper">
  //                           <div class="yotpo-star-distribution-index" aria-hidden="true">2</div>
  //                           <svg class="yotpo-star-rating-icon yotpo-sr-star-full" viewBox="0 0 18 18" xmlns="http://www.w3.org/2000/svg" transform="scale(1.2, 1.2)" style="width: 14px; height: 14px;">
  //                             <defs>
  //                                 <linearGradient id="yotpo_stars_gradient_0.7034368606232207">
  //                                   <stop offset="100%" stop-color="rgba(255,215,0,1)"></stop>
  //                                   <stop offset="100%" stop-color="#eaeaea" stop-opacity="1"></stop>
  //                                 </linearGradient>
  //                             </defs>
  //                             <path d="M9 14.118L14.562 17.475L13.086 11.148L18 6.891L11.529 6.342L9 0.375L6.471 6.342L0 6.891L4.914 11.148L3.438 17.475L9 14.118Z" fill="url(#yotpo_stars_gradient_0.7034368606232207)"></path>
  //                           </svg>
  //                       </div>
  //                       <div class="yotpo-star-distribution-bar">
  //                           <div class="yotpo-progress-bar">
  //                             <div class="yotpo-progress-bar-score" style="width: 0%;"></div>
  //                           </div>
  //                           <!---->
  //                       </div>
  //                       <div class="yotpo-star-distribution-value" aria-hidden="true">0</div>
  //                     </div>
  //                     <div class="yotpo-star-distribution-row-wrapper default-row yotpo-distribution-row" aria-label="1 star by 0 reviews" role="button" tabindex="0">
  //                       <div class="yotpo-star-distribution-index-wrapper">
  //                           <div class="yotpo-star-distribution-index" aria-hidden="true">1</div>
  //                           <svg class="yotpo-star-rating-icon yotpo-sr-star-full" viewBox="0 0 18 18" xmlns="http://www.w3.org/2000/svg" transform="scale(1.2, 1.2)" style="width: 14px; height: 14px;">
  //                             <defs>
  //                                 <linearGradient id="yotpo_stars_gradient_0.6155539364243255">
  //                                   <stop offset="100%" stop-color="rgba(255,215,0,1)"></stop>
  //                                   <stop offset="100%" stop-color="#eaeaea" stop-opacity="1"></stop>
  //                                 </linearGradient>
  //                             </defs>
  //                             <path d="M9 14.118L14.562 17.475L13.086 11.148L18 6.891L11.529 6.342L9 0.375L6.471 6.342L0 6.891L4.914 11.148L3.438 17.475L9 14.118Z" fill="url(#yotpo_stars_gradient_0.6155539364243255)"></path>
  //                           </svg>
  //                       </div>
  //                       <div class="yotpo-star-distribution-bar">
  //                           <div class="yotpo-progress-bar">
  //                             <div class="yotpo-progress-bar-score" style="width: 0%;"></div>
  //                           </div>
  //                           <!---->
  //                       </div>
  //                       <div class="yotpo-star-distribution-value" aria-hidden="true">0</div>
  //                     </div>
  //                 </div>
  //                 <!---->
  //               </div>
  //               <div data-v-012a3912="" class="yotpo-new-review-btn-wrapper "><button data-v-45e7fb23="" id="yotpo-main-widget-btn" type="button" class="yotpo-new-review-btn yotpo-btn-s yotpo-zero-review-write-button" style="color:white">Write A Review</button></div>
  //           </div>
  //           <!---->
  //         </div>`
  //         reviewWidgetHead.innerHTML=`<h2 class="yotpo-headline">Customer Reviews</h2>`
  //         emptyReviewContainer.innerHTML = widget;
  //       }
  //     }
  //   } else {
  //     // Add retry counter to prevent infinite retries
  //     const retryCount = window.yotpoRetryCount || 0;
  //     if (retryCount < 5) {
  //     window.yotpoRetryCount = retryCount + 1;
  //       setTimeout(() => {
  //         appendWidgetforReview();
  //       }, 3000);
  //     }
  //   }
  // }
  // appendWidgetforReview();

  function removeEmptyYotpoContainer() {
    const yotpoEl = document.querySelector(".yotpo-empty-state");
    if (yotpoEl) {
      // Find the ancestor <section> with class "shopify-section"
      const section = yotpoEl.closest("section.shopify-section");
      if (section) {
        // Hide the section
        section.style.display = "none";

        // Remove the next sibling <div>
        const nextDiv = section.nextElementSibling;
        if (nextDiv && nextDiv.tagName.toLowerCase() === "div") {
          nextDiv.remove();
        }
      }
    } else {
      const retryCount = window.yotpoRetryCount || 0;
      if (retryCount < 5) {
        window.yotpoRetryCount = retryCount + 1;
        setTimeout(() => {
          removeEmptyYotpoContainer();
        }, 3000);
      }
    }
  }

  removeEmptyYotpoContainer();
  
  // styling stars after click of reviews pagination
 function attachYotpoPaginationListener() {
    // Select all pagination buttons/links
    const paginationLinks = document.querySelectorAll(
      '.yotpo-reviews-pagination-item'
    );

    if (paginationLinks.length) {
      console.log('Pagination links found, attaching click listeners');

      paginationLinks.forEach(link => {
        link.addEventListener('click', () => {
          console.log('Pagination clicked → waiting for new content');
          // Wait for Yotpo to update reviews after click
          setTimeout(() => {
            applyStyling();
          }, 500); // Adjust delay if Yotpo loads slower
        });
      });
    } else {
      // Retry until links appear
      setTimeout(attachYotpoPaginationListener, 500);
    }
  }

  // Start listening
  attachYotpoPaginationListener();

  // Native JS click listener
  document.addEventListener('click', function (event) {
    if (event.target && event.target.id === 'review_yotpo_popup') {
      event.preventDefault();
      const reviewBtn = document.querySelector('.yotpo-new-review-btn');
      if (reviewBtn) {
        reviewBtn.click();
      } else {
        console.warn('Yotpo review trigger button not found');
      }
    }
  });

  document.addEventListener('click', function (event) {
    if (event.target?.classList.contains('yotpo-zero-review-write-button')) {
      event.preventDefault();
      const reviewBtn = document.querySelector('.yotpo-new-review-btn.empty-state');
      if (reviewBtn) {
        reviewBtn.click();
      } else {
        console.warn('Yotpo review trigger button not found');
      }
    }
  });  

  //function to hide review container on cards which does not have any reviews

  function hideRatingContainersWithoutScore() {
    let reviewsContainer = Array.from(document.querySelectorAll('.custom-collections-rating, .custom-card-rating, .custom-collections-rating'));
    reviewsContainer.forEach((container) => {
      if (!container.querySelector('.yotpo-sr-bottom-line-score')) {
        container.style.display = "none";
      }
    });
  }

  // Using intersection observer or mutation observer for more reliable timing
  const ratingObserver = new MutationObserver((mutations) => {
    mutations.forEach((mutation) => {
      if (mutation.type === 'childList') {
        const addedNodes = Array.from(mutation.addedNodes);

        addedNodes.forEach((node) => {
          if (
            node.classList?.contains('yotpo-widget-instance') &&
            node.querySelector('.product__info-container .yotpo-reviews-star-ratings-widget')
          ) {
            hideRatingContainersWithoutScore();
          }
        });
      }
    });
  });

  ratingObserver.observe(document.body, {
    childList: true,
    subtree: true
  });

  // Fallback: run once after 2 seconds
  setTimeout(hideRatingContainersWithoutScore, 2000);

});

const searchIcon = document.querySelector('.header__search--icon');
const drawerMenu = document.querySelector('#Details-menu-drawer-container');
const navbar = document.querySelector('.menu-drawer__navigation');
const header = document.querySelector('header');
const details = searchIcon?.closest('.header__search')?.querySelector('details');

function updateNavbarPadding() {
  const isMenuOpen = drawerMenu?.classList.contains('menu-opening');
  const isDetailsOpen = details?.hasAttribute('open');

  if (!isDetailsOpen) {
    navbar.style.padding = "3rem 0";
  } else if (isMenuOpen) {
    navbar.style.padding = "9rem 0";
  } else {
    navbar.style.padding = "3rem 0";
  }
}

function updateHeaderMargin() {
  const isDetailsOpen = details?.hasAttribute('open');
  if (isDetailsOpen) {
    header.classList.remove('.margin-bottom-zero');
    header.classList.add('.margin-bottom-nine');
  } else {
    header.classList.remove('.margin-bottom-nine');
    header.classList.add('.margin-bottom-zero');
  }
}

function updateUI() {
  updateNavbarPadding();
  updateHeaderMargin();
}

// Trigger when icon is clicked
searchIcon?.addEventListener('click', updateUI);

// Observe menu drawer open/close
if (drawerMenu) {
  const observer = new MutationObserver(updateUI);
  observer.observe(drawerMenu, {
    attributes: true,
    attributeFilter: ['class']
  });
}

// Observe details open/close
details?.addEventListener('toggle', updateUI);

window.showGiftcardPopup = (message) => {
  return new Promise((resolve) => {
    const popup = document.getElementById('giftcard-popup');
    const msg = document.getElementById('giftcard-popup-message');
    const yesBtn = document.getElementById('giftcard-popup-yes');
    const noBtn = document.getElementById('giftcard-popup-no');

    msg.textContent = message;
    popup.classList.remove('hidden');

    const cleanup = () => {
      popup.classList.add('hidden');
      yesBtn.removeEventListener('click', onYes);
      noBtn.removeEventListener('click', onNo);
    };

    const onYes = () => { cleanup(); resolve(true); };
    const onNo = () => { cleanup(); resolve(false); };

    yesBtn.addEventListener('click', onYes);
    noBtn.addEventListener('click', onNo);
  });
};

(() => {
  const OFFSET_FROM_BOTTOM = 120;
  const WHATSAPP_MOVE_UP = 90;
  const FOOTER_MOVE_UP = 25;

  const isMobile = () => window.innerWidth <= 768;

  const handleScroll = () => {
    if (!isMobile()) return;

    const whatsappIcon = document.querySelector('.WhatsAppButton__root');
    const footerCautionNotice = document.querySelector(
      '.footer-caution-notice-container'
    );

    const distanceFromBottom =
      document.documentElement.scrollHeight -
      (window.scrollY + window.innerHeight);

    const isNearBottom = distanceFromBottom < OFFSET_FROM_BOTTOM;

    if (whatsappIcon) {
      whatsappIcon.style.transform = isNearBottom
        ? `translateY(-${WHATSAPP_MOVE_UP}px)`
        : 'translateY(0)';
    }

    if (footerCautionNotice) {
      footerCautionNotice.style.transform = isNearBottom
        ? `translateY(-${FOOTER_MOVE_UP}px)`
        : 'translateY(0)';
    }
  };

  // Attach immediately
  window.addEventListener('scroll', handleScroll, { passive: true });

  // Run once on load
  window.addEventListener('load', handleScroll);
})();


document.addEventListener('DOMContentLoaded', function () {
  const bannerVideos = document.querySelectorAll(
    '.banner-video'
  );

  bannerVideos.forEach(function (video) {
    // Default state: muted autoplay
    video.muted = true;
    video.defaultMuted = true;
    video.loop = true;
    video.playsInline = true;

    video.play().catch(function () {});

    // Click to toggle audio
    video.addEventListener('click', function (event) {
      event.preventDefault();
      event.stopPropagation();

      if (video.muted) {
        // Mute other banner videos
        bannerVideos.forEach(function (otherVideo) {
          if (otherVideo !== video) {
            otherVideo.muted = true;
          }
        });

        video.muted = false;
        video.play().catch(function () {});
      } else {
        video.muted = true;
      }
    });
  });

  // Mute and pause videos outside the viewport
  const videoObserver = new IntersectionObserver(
    function (entries) {
      entries.forEach(function (entry) {
        const video = entry.target;

        if (entry.isIntersecting) {
          video.muted = true;
          video.play().catch(function () {});
        } else {
          video.muted = true;
          video.pause();
        }
      });
    },
    {
      threshold: 0.1
    }
  );

  bannerVideos.forEach(function (video) {
    videoObserver.observe(video);
  });
});